import { useContext, useMemo, useState, useEffect } from "react";
import "react-toastify/dist/ReactToastify.css";
import { useCookies } from "react-cookie";
import useFetch from "../../hooks/useFetch";
import { LiveDashboard } from "../../pages/Home/LiveDashboard";
import { SysAdmin } from "../../pages/SysAdmin/SysAdmin";
import DeviceService from "../../services/DeviceService";
import PatientService from "../../services/PatientService";
import ClinicService from "../../services/ClinicService";
import { Loading } from "../Loading";
import { ServerProblem } from "../../pages/ReRoute/ServerProblem";
import { UserContext } from "../../context/UserContext";
import { Device } from "../../interfaces/Device";
import { CCMPatient } from "../../interfaces/CCMPatient";
import { Box } from "@mui/material";
import { Cohort } from "../../interfaces/Clinic";
import { VICU } from "../../interfaces/VICU";
import UtilsService from "../../services/UtilsService";
import useAdminSocket from "../../hooks/useAdminSocket";
import EMSDashboardTabs from "./EMSDashboardTabs";
import RPMDashboardTabs from "./RPMDashboardTabs";
import CMSDashboardTabs from "./CMSDashboardTabs";
import { useTranslation } from "react-i18next";
import {
  HEARTBEAT_GREEN,
  HEARTBEAT_YELLOW,
  HEARTBEAT_GREY,
} from "../../pages/Home/MissionCentral";
import { useNavigate } from "react-router-dom";

const sortDevicesByName = (devices: Device[]): Device[] => {
  return devices.sort((a, b) => {
    const nameA =
      a.nickname?.trim() !== ""
        ? a.nickname.toLowerCase()
        : a.device_id.toLowerCase();
    const nameB =
      b.nickname?.trim() !== ""
        ? b.nickname.toLowerCase()
        : b.device_id.toLowerCase();

    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });
};

export const RPM_HISTORY_DAYS = 7; // number of days of historical engagemnet to return for "all patients" query

export const MainScreen = () => {
  const context = useContext(UserContext);
  const user = context?.user ?? null;
  const history = useNavigate();
  const [cookies, , removeCookie] = useCookies([
    "access_token",
    "refresh_token",
  ]);
  const [authHydrationChecked, setAuthHydrationChecked] =
    useState<boolean>(false);
  const [selectedCohort, setSelectedCohort] = useState<Cohort | null>(null);
  const [selectedVICU, setSelectedVICU] = useState<VICU | null>(null); // Keep for backward compatibility
  const [patientCohortMap, setPatientCohortMap] = useState<{
    [patientId: string]: number;
  }>({});
  const [cohortDataLoaded, setCohortDataLoaded] = useState<boolean>(false);

  const [visibleDevices, setVisibleDevices] = useState<Device[]>([]);
  const [visiblePatients, setVisiblePatients] = useState<CCMPatient[]>([]);
  var token = cookies.access_token;
  const [adminDataPacket, setAdminDataPacket] = useState<any>(null);
  const [monitorGroupList, setMonitorGroupList] = useState<any>([]);
  const [currentMonitorGroupList, setCurrentMonitorGroupList] =
    useState<any>(null);
  const [selectedVariant, setSelectedVariant] = useState<string>("");
  const [useSyspool] = useState<boolean>(false);
  const [selectedClinic, setSelectedClinic] = useState<string | undefined>(
    () => {
      // Try to restore from localStorage first
      const saved = localStorage.getItem("selectedClinic");
      console.log("Initial localStorage selectedClinic:", saved);
      return saved || undefined;
    },
  );
  const [cohortList, setCohortList] = useState<any[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<number>(Infinity);
  const { t } = useTranslation();

  useEffect(() => {
    if (!context) {
      setAuthHydrationChecked(true);
      return;
    }

    if (context.user) {
      setAuthHydrationChecked(true);
      return;
    }

    if (!cookies.access_token) {
      setAuthHydrationChecked(true);
      return;
    }

    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        context.setUser(JSON.parse(storedUser));
      } catch (e) {
        console.warn("Failed to parse stored user, forcing login", e);
        localStorage.removeItem("user");
        removeCookie("access_token");
        removeCookie("refresh_token");
        history("/login", { replace: true });
      }
    } else {
      removeCookie("access_token");
      removeCookie("refresh_token");
      history("/login", { replace: true });
    }

    setAuthHydrationChecked(true);
  }, [context, cookies.access_token, history, removeCookie]);

  const my_organization_id = user?.organization || "";

  const updateDeviceList = () => {
    refreshDevices();
  };

  // Data for EMS version: Devices
  const {
    data: devices,
    setData: setDevices,
    refresh: refreshDevices,
    loading,
    error,
  } = useFetch(
    selectedVariant === "EMS"
      ? {
          url: DeviceService.getDevices(),
          access_token: token,
        }
      : { url: "", access_token: "" },
  );

  // Data for RPM version: Patients
  const patientsRequest = useMemo(() => {
    if (selectedVariant !== "RPM" || !my_organization_id || !token) {
      return null;
    }
    return {
      url: PatientService.getPatients(my_organization_id, RPM_HISTORY_DAYS),
      access_token: token,
    };
  }, [selectedVariant, my_organization_id, token]);

  // Only invoke useFetch if patientsRequest is valid
  const { data: patients, refresh: refreshPatients } = useFetch(
    patientsRequest || { url: "", access_token: "" },
  );

  // Log raw patient data from backend
  useEffect(() => {
    if (patients && selectedVariant === "RPM") {
      console.log(
        "Raw patient data from PatientService.getPatients (MainScreen):",
        patients,
      );
      console.log("Patient count:", patients.length);
      if (patients.length > 0) {
        console.log("Sample patient object (first patient):", patients[0]);
        console.log("Patient object keys:", Object.keys(patients[0]));

        // Check cohort_id distribution
        const cohortIds = patients.map((p: any) => p.cohort_id).filter(Boolean);
        console.log("Patients with cohort_id:", cohortIds);
        console.log("Unique cohort_ids:", Array.from(new Set(cohortIds)));
      }
    }
  }, [patients, selectedVariant]);

  //console.log ("SelectedVariant is ", selectedVariant);

  const monitorGroupsRequest = useMemo(() => {
    return {
      url: ClinicService.getAllGroups(),
      access_token: cookies.access_token,
    };
  }, [cookies.access_token]);

  const {
    data: monitorGroups,
    loading: monitorGroupLoading,
    error: monitorGroupError,
  } = useFetch(monitorGroupsRequest);

  // Fetch clinics for dropdown
  const clinicsRequest = useMemo(() => {
    return {
      url: ClinicService.getAllClinics(undefined, false), // Don't need cohorts for dropdown
      access_token: cookies.access_token,
    };
  }, [cookies.access_token]);

  const { data: clinicsData } = useFetch(clinicsRequest);

  const getSelectedClinic = (): [string | undefined, string | undefined] => {
    const savedData = localStorage.getItem("selectedClinicVICU");
    if (savedData) {
      const clinicDict: { [key: string]: string } = JSON.parse(savedData);
      const savedClinic = clinicDict["clinic"];
      if (savedClinic) {
        const savedVicu = clinicDict[savedClinic];
        return [savedClinic, savedVicu];
      }
    }
    return [undefined, undefined];
  };

  useEffect(() => {
    if (monitorGroups === null) {
      return;
    }
    console.log("monitorGroups:", monitorGroups);
    console.log(
      "monitorGroups type:",
      typeof monitorGroups,
      "isArray:",
      Array.isArray(monitorGroups),
    );
    // Handle both old format (direct array) and new format (with results array)
    const groupsArray = Array.isArray(monitorGroups)
      ? monitorGroups
      : monitorGroups.results || [];
    console.log("groupsArray:", groupsArray);
    // Filter out any monitor group with id "syspool"
    const filteredMonitorGroupList = groupsArray.filter(
      (group: any) => group.group_name !== "syspool",
    );

    setCurrentMonitorGroupList(filteredMonitorGroupList); // Set the current monitor group list
    //console.log("filteredMonitorGroupList:", filteredMonitorGroupList);

    // Update the selected clinic if it's not set or if it's invalid
    if (
      !selectedClinic ||
      !filteredMonitorGroupList.some(
        (group: any) => group.group_id === selectedClinic,
      )
    ) {
      const [clinic, vicuId] = getSelectedClinic();
      //console.log("selectedClinic:", clinic, vicuId);
      if (clinic) {
        setSelectedClinic(clinic);
        const clinicData = filteredMonitorGroupList.find(
          (group: any) => group.group_id === clinic,
        );
        if (clinicData) {
          const vicuData = clinicData.children.find(
            (child: any) => child.group_id === vicuId,
          );
          if (vicuData) {
            setSelectedVICU(vicuData);
            setMonitorGroupList(clinicData.children);
          } else {
            setMonitorGroupList(clinicData.children);
            setSelectedVICU(clinicData.children[0]);
          }
        }
      } else {
        if (filteredMonitorGroupList.length > 0) {
          const activeMonitorList = filteredMonitorGroupList[0].children;
          if (activeMonitorList && activeMonitorList.length > 0) {
            setMonitorGroupList(activeMonitorList);
            setSelectedVICU(activeMonitorList[0]);
            // Instead of only setting selectedClinic, trigger the clinic-change handler so cohorts are fetched
            const firstClinicId = filteredMonitorGroupList[0].group_id;
            // call the handler defined below after the render cycle
            setTimeout(() => handleClinicChange(firstClinicId), 0);
          }
        }
      }
    }

    // TODO: check this exception. It is due to selectedRegion
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monitorGroups]);

  // Update visibleDevices when selectedClinic or selectedVICU changes
  useEffect(() => {
    if (selectedClinic && currentMonitorGroupList) {
      const clinicData = currentMonitorGroupList.find(
        (group: any) => group.group_id === selectedClinic,
      );
      if (clinicData) {
        const [, vicuId] = getSelectedClinic();
        const vicuData = clinicData.children.find(
          (child: any) => child.group_id === vicuId,
        );
        if (vicuData) {
          setSelectedVICU(vicuData);
          setMonitorGroupList(clinicData.children);
        } else {
          setMonitorGroupList(clinicData.children);
          setSelectedVICU(clinicData.children[0]);
        }
      }
    } else {
      // Clear visible devices and cohorts when no clinic is selected
      setMonitorGroupList([]);
      setVisibleDevices([]);
    }
  }, [selectedClinic, currentMonitorGroupList]);

  // Update visibleDevices based on the selected VICU
  useEffect(() => {
    if (selectedVICU) {
      const sortedDevices = sortDevicesByName(selectedVICU.devices);
      setVisibleDevices(sortedDevices);
    } else {
      // Clear visible devices if no VICU is selected
      setVisibleDevices([]);
    }
  }, [selectedVICU]);

  // Update visiblePatients based on the selected Cohort
  useEffect(() => {
    console.log("=== PATIENT FILTERING DEBUG ===");
    console.log("selectedCohort:", selectedCohort);
    console.log("patients array:", patients);
    console.log("patients length:", patients?.length);
    console.log("selectedVariant:", selectedVariant);
    console.log("cohortDataLoaded:", cohortDataLoaded);
    console.log("patientCohortMap keys:", Object.keys(patientCohortMap));

    // For RPM variant, don't show patients until cohort data has been loaded
    if (
      selectedVariant === "RPM" &&
      patients &&
      Array.isArray(patients) &&
      !cohortDataLoaded
    ) {
      console.log("RPM variant - Waiting for cohort data to be loaded...");
      setVisiblePatients([]);
      return;
    }

    // For non-RPM variants, show all patients (no cohort filtering)
    if (selectedVariant !== "RPM" && patients && Array.isArray(patients)) {
      console.log("Non-RPM variant, showing all patients:", patients.length);
      setVisiblePatients(patients);
      return;
    }

    if (selectedCohort && patients && Array.isArray(patients)) {
      console.log("Filtering patients for cohort:", selectedCohort.name);
      console.log(
        "Selected cohort ID:",
        (selectedCohort as any).cohort_id || selectedCohort.id,
      );
      console.log("Patient-cohort mapping:", patientCohortMap);

      // Augment patients with cohort_id from our mapping
      const patientsWithCohorts = patients.map((patient: any) => ({
        ...patient,
        cohort_id: patientCohortMap[patient.patient_id] || null,
      }));

      // Check what cohort_ids exist in the patient data
      const patientCohortIds = patientsWithCohorts.map((p: any) => ({
        id: p.patient_id,
        name: `${p.first_name} ${p.last_name}`,
        cohort_id: p.cohort_id,
      }));
      console.log("Patient cohort IDs (after mapping):", patientCohortIds);

      // Filter patients that belong to the selected cohort
      const filteredPatients = patientsWithCohorts.filter((patient: any) => {
        // Convert both IDs to strings for comparison
        const patientCohortId = String(patient.cohort_id || "");
        const selectedCohortId = String(
          (selectedCohort as any).cohort_id || selectedCohort.id || "",
        );
        const match = patientCohortId === selectedCohortId;

        console.log(
          `Patient ${patient.first_name} ${patient.last_name}: cohort_id="${patientCohortId}" vs selected="${selectedCohortId}" = ${match}`,
        );

        return match;
      });

      // Sort filtered patients by patient_id (or name if available)
      const sortedFilteredPatients = filteredPatients.sort(
        (a: CCMPatient, b: CCMPatient) => {
          const nameA = (a.patient_id || "").toLowerCase();
          const nameB = (b.patient_id || "").toLowerCase();
          return nameA.localeCompare(nameB);
        },
      );

      console.log(
        "Filtered patients for cohort",
        selectedCohort.name + ":",
        sortedFilteredPatients.length,
        "out of",
        patients.length,
      );
      console.log(
        "Filtered patients:",
        sortedFilteredPatients.map(
          (p: any) => `${p.first_name} ${p.last_name}`,
        ),
      );
      console.log(
        "Setting visiblePatients to:",
        sortedFilteredPatients.length,
        "patients",
      );
      setVisiblePatients(sortedFilteredPatients);
    } else if (selectedVICU) {
      // For backward compatibility, convert VICU devices to patients if needed
      // This would be handled based on the specific use case
      setVisiblePatients([]);
    } else {
      // If no cohort is selected, show unassigned patients (those without cohort_id)
      if (patients && Array.isArray(patients)) {
        // Augment patients with cohort_id from our mapping
        const patientsWithCohorts = patients.map((patient: any) => ({
          ...patient,
          cohort_id: patientCohortMap[patient.patient_id] || null,
        }));

        const unassignedPatients = patientsWithCohorts.filter(
          (patient: any) => !patient.cohort_id || patient.cohort_id === "",
        );

        // Sort unassigned patients by patient_id
        const sortedUnassignedPatients = unassignedPatients.sort(
          (a: CCMPatient, b: CCMPatient) => {
            const nameA = (a.patient_id || "").toLowerCase();
            const nameB = (b.patient_id || "").toLowerCase();
            return nameA.localeCompare(nameB);
          },
        );

        console.log(
          "No cohort selected, showing unassigned patients:",
          sortedUnassignedPatients.length,
          "out of",
          patients.length,
        );
        console.log(
          "Unassigned patients:",
          sortedUnassignedPatients.map(
            (p: any) => `${p.first_name} ${p.last_name}`,
          ),
        );
        setVisiblePatients(sortedUnassignedPatients);
      } else {
        setVisiblePatients([]);
      }
    }
  }, [
    selectedCohort,
    selectedVICU,
    patients,
    patientCohortMap,
    cohortDataLoaded,
    selectedVariant,
  ]);

  const dropdownOptions = useMemo(() => {
    if (!currentMonitorGroupList) return [];
    return currentMonitorGroupList
      .filter(
        (group: any) =>
          group.group_name && group.group_name !== "syspool" && group.group_id,
      ) // Filter out groups with name "syspool" and ensure group_name and group_id exist
      .sort((a: any, b: any) =>
        (a.group_name || "").localeCompare(b.group_name || ""),
      )
      .map((group: any) => ({
        value: String(group.group_id),
        label: group.group_name,
      }));
  }, [currentMonitorGroupList]);

  // Clinic dropdown options for CCM Central
  const clinicDropdownOptions = useMemo(() => {
    if (!clinicsData || !clinicsData.results) return [];
    console.log("Clinic dropdown data:", clinicsData.results);
    if (clinicsData.results.length > 0) {
      console.log("First clinic keys:", Object.keys(clinicsData.results[0]));
      console.log("First clinic sample:", clinicsData.results[0]);
    }
    return clinicsData.results
      .filter(
        (clinic: any) =>
          clinic && (clinic.clinic_id !== undefined || clinic.id !== undefined),
      )
      .sort((a: any, b: any) => (a.name || "").localeCompare(b.name || ""))
      .map((clinic: any) => {
        console.log("Mapping clinic:", clinic);
        return {
          value: String(clinic.clinic_id || clinic.id),
          label: clinic.name || "Unnamed Clinic",
        };
      });
  }, [clinicsData]);

  const filterOptions_EMS = [
    { label: t("live"), value: HEARTBEAT_GREEN },
    { label: t("last_10_minutes"), value: HEARTBEAT_YELLOW },
    { label: t("last_24_hours"), value: HEARTBEAT_GREY },
    { label: t("all"), value: Infinity },
  ];

  useEffect(() => {
    if (selectedClinic && selectedVICU) {
      const storedData = JSON.parse(
        localStorage.getItem("selectedClinicVICU") || "{}",
      );
      storedData[selectedClinic] = selectedVICU.group_id;
      storedData["clinic"] = selectedClinic;
      localStorage.setItem("selectedClinicVICU", JSON.stringify(storedData));
    }
  }, [selectedClinic, selectedVICU]);

  // Clinic Dropdown change callback
  const handleClinicChange = (value: string) => {
    console.log("HANDLE-CHANGE: Selected clinic:", value);
    setSelectedClinic(value);
    setCohortDataLoaded(false); // Reset flag when changing clinics
    // Save clinic selection to localStorage
    localStorage.setItem("selectedClinic", value);
    console.log("HANDLE-CHANGE: Saved to localStorage:", value);

    // For RPM/CCM Central, fetch cohorts when a clinic is selected
    if (selectedVariant === "RPM" && value) {
      // Fetch cohorts for the selected clinic
      const cohortsUrl = ClinicService.getAllCohorts(value, undefined, true); // Filter by clinic_id and include patients

      fetch(cohortsUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
        .then((res) => {
          console.log(
            "Cohorts response for clinic change:",
            res.status,
            "url:",
            cohortsUrl,
          );
          return res.json();
        })
        .then((data) => {
          console.log("Fetched cohorts for clinic:", data);
          const cohortsArray = Array.isArray(data) ? data : data.results || [];
          console.log("Cohorts array:", cohortsArray);

          // Build patient-cohort mapping from the cohort associations
          const newPatientCohortMap: { [patientId: string]: number } = {};
          cohortsArray.forEach((cohort: any, index: number) => {
            console.log(
              `Cohort ${index}:`,
              cohort,
              "ID:",
              cohort.cohort_id || cohort.id,
              "Name:",
              cohort.name,
            );
            console.log(
              `Processing cohort ${cohort.name} for patient associations:`,
              cohort.patient_associations,
            );

            if (
              cohort.patient_associations &&
              Array.isArray(cohort.patient_associations)
            ) {
              cohort.patient_associations.forEach((assoc: any) => {
                if (assoc.is_active) {
                  newPatientCohortMap[assoc.patient_id] = cohort.cohort_id;
                  console.log(
                    `✅ Mapped patient ${assoc.patient_name} (${assoc.patient_id}) -> Cohort ${cohort.name} (${cohort.cohort_id})`,
                  );
                }
              });
            }
          });

          console.log("Built patient-cohort mapping:", newPatientCohortMap);
          setPatientCohortMap(newPatientCohortMap);
          setCohortDataLoaded(true);
          setCohortList(cohortsArray);

          // Try to restore saved cohort selection for this clinic
          const savedCohortKey = `selectedCohort_${value}`;
          const savedCohort = localStorage.getItem(savedCohortKey);
          console.log(
            "🔄 Clinic change cohort restoration - Key:",
            savedCohortKey,
            "Saved data:",
            savedCohort,
          );

          let cohortSelected = false;
          if (savedCohort && cohortsArray.length > 0) {
            try {
              const parsedCohort = JSON.parse(savedCohort);
              console.log(
                "📝 Parsed saved cohort for clinic change:",
                parsedCohort,
              );
              const foundCohort = cohortsArray.find((c: any) => {
                // Priority 1: Match by cohort_id (most reliable)
                if (c.cohort_id === parsedCohort.cohort_id) {
                  return true;
                }

                // Priority 2: Match by name (fallback for old saves)
                if (c.name === parsedCohort.name) {
                  return true;
                }

                // Priority 3: Cross-field matches (only if not undefined)
                if (
                  parsedCohort.id !== undefined &&
                  c.cohort_id === parsedCohort.id
                ) {
                  return true;
                }

                if (
                  parsedCohort.cohort_id !== undefined &&
                  c.id === parsedCohort.cohort_id
                ) {
                  return true;
                }

                return false;
              });
              if (foundCohort) {
                console.log(
                  "✅ Restored saved cohort for new clinic:",
                  foundCohort,
                );
                setSelectedCohort(foundCohort);
                cohortSelected = true;
              } else {
                console.log(
                  "❌ Saved cohort not found in new clinic's cohort list",
                );
              }
            } catch (e) {
              console.warn("Failed to parse saved cohort:", e);
            }
          }

          // Only auto-select first cohort if no saved selection was restored
          if (!cohortSelected) {
            if (cohortsArray.length > 0) {
              console.log(
                "📌 Auto-selecting first cohort for new clinic:",
                cohortsArray[0],
              );
              setSelectedCohort(cohortsArray[0]);
            } else {
              console.log("📌 No cohorts available for new clinic");
              setSelectedCohort(null);
            }
          }
        })
        .catch((err) => {
          console.error("Error fetching cohorts:", err);
          setCohortList([]);
          setSelectedCohort(null);
          setCohortDataLoaded(true); // Even if no cohorts, we've loaded the data
          setPatientCohortMap({}); // Clear the mapping
        });
    }

    // Patient filtering is now handled by the cohort filtering effect below
    // No need to set all patients visible here since we have proper cohort-based filtering

    // For EMS variant, handle clinic children logic
    if (selectedVariant === "EMS" && currentMonitorGroupList) {
      const filteredClinics = currentMonitorGroupList.filter(
        (group: any) => group.group_id !== "syspool",
      );
      const clinic = filteredClinics.find(
        (group: any) => group.group_id === value,
      );

      if (clinic && clinic.children) {
        const clinicChildren = clinic.children;
        console.log("Clinic children:", clinicChildren);
        setMonitorGroupList(clinicChildren);

        if (clinicChildren.length === 0) {
          setVisibleDevices([]);
          return;
        }

        const savedData = JSON.parse(
          localStorage.getItem("selectedClinicVICU") || "{}",
        );
        if (savedData[value]) {
          const savedVICU = clinicChildren.find(
            (child: any) => child.group_id === savedData[value],
          );
          if (savedVICU) {
            setSelectedVICU(savedVICU);
          } else {
            setSelectedVICU(clinicChildren[0]);
          }
        } else {
          setSelectedVICU(clinicChildren[0]);
        }
      }
    }
  };

  const handleFilterChange = (value: number) => {
    //console.log ("Filter value:", value);
    setSelectedFilter(value);
  };

  const handleCohortChange = (cohort: any) => {
    console.log("🔄 handleCohortChange called with cohort:", cohort);
    console.log("Previous selectedCohort:", selectedCohort);
    setSelectedCohort(cohort);
    // Save cohort selection to localStorage with clinic-specific key
    if (selectedClinic && cohort) {
      const cohortData = {
        cohort_id: (cohort as any).cohort_id || cohort.id,
        id: (cohort as any).id || (cohort as any).cohort_id, // Ensure id is always present
        name: cohort.name,
      };
      const key = `selectedCohort_${selectedClinic}`;
      localStorage.setItem(key, JSON.stringify(cohortData));
      console.log("💾 Saved cohort to localStorage:", key, "=>", cohortData);
    } else {
      console.log(
        "⚠️ Cannot save cohort - selectedClinic:",
        selectedClinic,
        "cohort:",
        cohort,
      );
    }
  };

  const {
    data: systemType,
    loading: systemTypeLoading,
    error: systemTypeError,
  } = useFetch({
    url: UtilsService.getSystemType(),
    access_token: token,
  });

  useEffect(() => {
    if (systemType) {
      localStorage.setItem("SYSTEM_TYPE", systemType["system_type"]);
    }
  }, [systemType]);

  useEffect(() => {
    const variant = user?.sentio_variant || "";
    console.log(
      "Setting variant - user:",
      user,
      "sentio_variant:",
      user?.sentio_variant,
      "final variant:",
      variant,
    );
    setSelectedVariant(variant);
  }, [user]);

  // Fallback: If we have clinic data but no user variant, assume RPM
  useEffect(() => {
    if (!selectedVariant && clinicsData?.results?.length > 0) {
      console.log("No variant set but have clinic data, defaulting to RPM");
      setSelectedVariant("RPM");
    }
  }, [selectedVariant, clinicsData]);

  useAdminSocket({ setAdminDataPacket });

  useEffect(() => {
    if (adminDataPacket === null) {
      return;
    }
    let msgType = adminDataPacket["type"];
    let message = adminDataPacket["message"];
    let opcode = message["opcode"];
    console.log(message);
    switch (msgType) {
      case "admin-update":
        switch (opcode) {
          case "user-access":
            if (user?.username !== message["username"]) {
              return;
            }
            let groups = JSON.parse(message["assigned"]);
            setCurrentMonitorGroupList(groups); // Update currentMonitorGroupList when "user-access" event is received
            setMonitorGroupList(groups);
            break;

          case "group-update":
            setMonitorGroupList((prevMonitorGroupList: any) => {
              const updatedMonitorGroupList = prevMonitorGroupList.map(
                (monGroup: any) => {
                  if (monGroup.group_id === message.group_id) {
                    const updatedMonGroup = {
                      ...monGroup,
                      devices: message.devices,
                    };
                    if (selectedVICU?.group_id === message.group_id) {
                      setVisibleDevices(updatedMonGroup.devices);
                    }
                    return updatedMonGroup;
                  }
                  return monGroup;
                },
              );
              return updatedMonitorGroupList;
            });
            break;
        }
        break;
      default:
        console.log("Admin data packet:", adminDataPacket);
    }
  }, [adminDataPacket, selectedVICU?.group_id, user?.username]);

  // Fetch cohorts on initial load if a clinic is already selected
  useEffect(() => {
    console.log(
      "Cohort fetch effect - selectedVariant:",
      selectedVariant,
      "selectedClinic:",
      selectedClinic,
      "token:",
      !!token,
    );
    console.log("User sentio_variant:", user?.sentio_variant);
    // Fetch cohorts if variant is RPM OR if user is RPM but variant not set yet
    const isRPMUser =
      user?.sentio_variant === "RPM" || selectedVariant === "RPM";
    console.log(
      "isRPMUser:",
      isRPMUser,
      "will fetch cohorts:",
      !!(selectedClinic && token && isRPMUser),
    );
    if (selectedClinic && token && isRPMUser) {
      console.log("Fetching cohorts for clinic:", selectedClinic);
      // Use the correct endpoint to get cohorts filtered by clinic_id
      const cohortsUrl = ClinicService.getAllCohorts(
        selectedClinic,
        undefined,
        true,
      ); // Filter by clinic_id and include patients

      fetch(cohortsUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
        .then((res) => {
          console.log(
            "Cohorts response status:",
            res.status,
            "url:",
            cohortsUrl,
          );
          return res.json();
        })
        .then((data) => {
          console.log(
            "🎯 Initial cohorts fetch for clinic:",
            selectedClinic,
            data,
          );
          const cohortsArray = Array.isArray(data) ? data : data.results || [];
          console.log("🎯 Initial cohorts array:", cohortsArray);
          console.log("🎯 About to start cohort restoration process...");

          // Build patient-cohort mapping from the cohort associations
          const newPatientCohortMap: { [patientId: string]: number } = {};
          cohortsArray.forEach((cohort: any) => {
            console.log(
              `Processing cohort ${cohort.name} for patient associations:`,
              cohort.patient_associations,
            );

            if (
              cohort.patient_associations &&
              Array.isArray(cohort.patient_associations)
            ) {
              cohort.patient_associations.forEach((assoc: any) => {
                if (assoc.is_active) {
                  newPatientCohortMap[assoc.patient_id] = cohort.cohort_id;
                  console.log(
                    `✅ Mapped patient ${assoc.patient_name} (${assoc.patient_id}) -> Cohort ${cohort.name} (${cohort.cohort_id})`,
                  );
                }
              });
            }
          });

          console.log(
            "Built patient-cohort mapping (initial load):",
            newPatientCohortMap,
          );
          setPatientCohortMap(newPatientCohortMap);
          setCohortDataLoaded(true);
          setCohortList(cohortsArray);

          console.log(
            "🎯 Starting cohort restoration for selectedClinic:",
            selectedClinic,
          );

          // Debug: Show all localStorage keys
          const allKeys = Object.keys(localStorage);
          console.log("🔍 All localStorage keys:", allKeys);
          const cohortKeys = allKeys.filter((key) =>
            key.startsWith("selectedCohort_"),
          );
          console.log("🔍 Cohort-related localStorage keys:", cohortKeys);

          // Try to restore saved cohort selection for this clinic
          const savedCohortKey = `selectedCohort_${selectedClinic}`;
          const savedCohort = localStorage.getItem(savedCohortKey);
          console.log(
            "🔄 Attempting cohort restoration - Key:",
            savedCohortKey,
            "Saved data:",
            savedCohort,
          );

          let cohortSelected = false;
          if (savedCohort && cohortsArray.length > 0) {
            try {
              const parsedCohort = JSON.parse(savedCohort);
              console.log("📝 Parsed saved cohort:", parsedCohort);

              console.log("🔍 Available cohorts for matching:");
              cohortsArray.forEach((c: any, index: number) => {
                console.log(
                  `  Cohort ${index}: name="${c.name}", id="${c.id}", cohort_id="${c.cohort_id}"`,
                );
              });

              const foundCohort = cohortsArray.find((c: any) => {
                // Priority 1: Match by cohort_id (most reliable)
                const match2 = c.cohort_id === parsedCohort.cohort_id;
                if (match2) {
                  console.log(
                    `✅ MATCH: cohort_id ${c.cohort_id} === ${parsedCohort.cohort_id}`,
                  );
                  return true;
                }

                // Priority 2: Match by name (fallback for old saves)
                const nameMatch = c.name === parsedCohort.name;
                if (nameMatch) {
                  console.log(
                    `✅ MATCH: name "${c.name}" === "${parsedCohort.name}"`,
                  );
                  return true;
                }

                // Priority 3: Cross-field matches (only if not undefined)
                const match3 =
                  parsedCohort.id !== undefined &&
                  c.cohort_id === parsedCohort.id;
                const match4 =
                  parsedCohort.cohort_id !== undefined &&
                  c.id === parsedCohort.cohort_id;

                if (match3) {
                  console.log(
                    `✅ MATCH: cross-field cohort_id ${c.cohort_id} === saved id ${parsedCohort.id}`,
                  );
                  return true;
                }

                if (match4) {
                  console.log(
                    `✅ MATCH: cross-field id ${c.id} === saved cohort_id ${parsedCohort.cohort_id}`,
                  );
                  return true;
                }

                console.log(`❌ No match for cohort "${c.name}"`);
                return false;
              });

              if (foundCohort) {
                console.log("✅ Restored saved cohort:", foundCohort);
                setSelectedCohort(foundCohort);
                cohortSelected = true;
              } else {
                console.log("❌ Saved cohort not found in current cohort list");
                console.log(
                  "Available cohorts:",
                  cohortsArray.map(
                    (c: any) =>
                      `${c.name} (id: ${c.id || "none"}, cohort_id: ${c.cohort_id || "none"})`,
                  ),
                );
              }
            } catch (e) {
              console.warn("Failed to parse saved cohort:", e);
            }
          } else {
            console.log("No saved cohort found or no cohorts available");
          }

          // Only auto-select first cohort if no saved selection was restored
          if (!cohortSelected) {
            if (cohortsArray.length > 0) {
              console.log("📌 Auto-selecting first cohort:", cohortsArray[0]);
              setSelectedCohort(cohortsArray[0]);
            } else {
              console.log("📌 No cohorts available, setting to null");
              setSelectedCohort(null);
            }
          }
        })
        .catch((err) => {
          console.error("🚨 Error fetching initial cohorts:", err);
          console.error("🚨 Error details:", {
            message: err.message,
            stack: err.stack,
            selectedClinic,
            cohortsUrl,
            token: !!token,
          });
          setCohortList([]);
          setSelectedCohort(null);
          setCohortDataLoaded(true); // Even if error, we've attempted to load
          setPatientCohortMap({}); // Clear the mapping
        });
    }
  }, [selectedVariant, selectedClinic, token, clinicsData, user]); // Trigger when variant, clinic, token, clinicsData, or user changes

  // Initialize clinic selection on first load
  useEffect(() => {
    console.log(
      "Clinic initialization - selectedVariant:",
      selectedVariant,
      "clinicsData:",
      clinicsData,
      "selectedClinic:",
      selectedClinic,
    );
    if (
      selectedVariant === "RPM" &&
      clinicsData?.results &&
      clinicsData.results.length > 0 &&
      !selectedClinic
    ) {
      // Auto-select first clinic if no clinic is currently selected
      const firstClinic = clinicsData.results[0];
      console.log("First clinic data:", firstClinic);
      console.log("Available keys in clinic:", Object.keys(firstClinic));
      console.log(
        "clinic.id:",
        firstClinic.id,
        "clinic.clinic_id:",
        firstClinic.clinic_id,
      );
      const clinicId = firstClinic.clinic_id || firstClinic.id;
      console.log("Extracted clinic ID:", clinicId, "type:", typeof clinicId);
      if (firstClinic && clinicId !== undefined && clinicId !== null) {
        console.log("Auto-selecting first clinic:", firstClinic);
        const clinicIdString = String(clinicId); // Use String() instead of .toString() for safety
        console.log("AUTO-SELECT: Setting selectedClinic to:", clinicIdString);
        setSelectedClinic(clinicIdString);
        localStorage.setItem("selectedClinic", clinicIdString);
        console.log("AUTO-SELECT: Saved to localStorage:", clinicIdString);
      } else {
        console.warn(
          "First clinic has invalid ID:",
          firstClinic,
          "clinicId:",
          clinicId,
        );
      }
    }
  }, [selectedVariant, clinicsData, selectedClinic]);

  // RPM / CCM implementations - Patient sorting and filtering is now handled by the cohort filtering effect
  // This ensures patients are filtered by cohort first, then sorted appropriately

  if (user?.is_superuser) {
    return <SysAdmin />;
  }
  if (systemType && systemType["system_type"] === "PHARLAP") {
    return (
      <LiveDashboard
        devicelist={devices}
        organization_id={user?.organization || ""}
        filterValue={selectedFilter}
      />
    );
  }

  console.log("MainScreen context check:", {
    context: context,
    contextUser: context?.user,
    localUser: user,
    cookies: cookies.access_token ? "present" : "missing",
  });

  if (!authHydrationChecked) {
    return <Loading />;
  }

  if (!context || !context.user) {
    return <div>Loading or not available...</div>;
  }

  if (loading || systemTypeLoading || monitorGroupLoading) {
    return <Loading />;
  }

  if (error || systemTypeError || monitorGroupError) {
    return <ServerProblem />;
  }

  //console.log("patients = ", patients);
  return (
    <Box sx={{ mx: 2, mt: 2 }}>
      {selectedVariant === "EMS" && (
        <EMSDashboardTabs
          visibleDevices={visibleDevices}
          organization_id={user?.organization || ""}
          monitorGroupList={monitorGroupList}
          setSelectedVICU={setSelectedVICU}
          selectedVICU={selectedVICU}
          updateDeviceList={updateDeviceList}
          setDevices={setDevices}
          clinicOptions={dropdownOptions}
          clinicDropdownTitle={t("clinic")}
          selectedClinicItem={selectedClinic}
          onClinicChange={handleClinicChange}
          filterOptions={filterOptions_EMS}
          filterDropdownTitle={t("activity_filter")}
          selectedFilterItem={selectedFilter}
          onFilterChange={handleFilterChange}
          useSyspool={useSyspool}
        />
      )}
      {selectedVariant === "RPM" && (
        <RPMDashboardTabs
          visibleDevices={visibleDevices}
          visiblePatients={visiblePatients}
          organization_id={user?.organization || ""}
          monitorGroupList={monitorGroupList}
          setSelectedVICU={setSelectedVICU}
          selectedVICU={selectedVICU}
          updateDeviceList={updateDeviceList}
          setDevices={setDevices}
          useSyspool={useSyspool}
          refreshPatients={refreshPatients}
          clinicOptions={clinicDropdownOptions}
          clinicDropdownTitle={t("clinic")}
          selectedClinicItem={selectedClinic}
          onClinicChange={handleClinicChange}
          filterOptions={[]}
          filterDropdownTitle={""}
          selectedFilterItem={Infinity}
          onFilterChange={handleFilterChange}
          cohortList={cohortList}
          selectedCohort={selectedCohort}
          onCohortChange={handleCohortChange}
        />
      )}
      {selectedVariant === "CMS" && (
        <CMSDashboardTabs
          visibleDevices={visibleDevices}
          organization_id={user?.organization || ""}
          monitorGroupList={monitorGroupList}
          setSelectedVICU={setSelectedVICU}
          selectedVICU={selectedVICU}
          updateDeviceList={updateDeviceList}
          setDevices={setDevices}
          useSyspool={useSyspool}
        />
      )}
    </Box>
  );
};
